import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { shallowEqual, useSelector } from 'react-redux';
import forge from 'apis/forge';
import eventManager from 'events/EventManager';
import Icon, { IconType } from 'components/Icon';
import ProfileIcon from 'components/Panel/RightPanel/ProfileIcon';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { CommentFrameEntry, Frame } from 'dragonmouse-shared/interfaces';
import { selectFrameCommentAuthor } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { formatRelativeTime } from 'utils';
import * as S from './styles';

interface CommentProps {
	comment: CommentFrameEntry;
	frame: Frame;
	isReply?: boolean;
	onStartReply?: () => void;
}

const Comment = ({ comment, frame, isReply, onStartReply }: CommentProps) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editedMessage, setEditedMessage] = useState(comment.message);

	const { user, authUser, inspectorRightPanelWidth } = useSelector(
		(r: RootReducer) => ({
			user: selectFrameCommentAuthor(r.db, comment.userId),
			authUser: r.mode.authUser,
			inspectorRightPanelWidth: r.mode.inspectorRightPanelWidth,
		}),
		shallowEqual
	);

	const displayName = user?.displayName || 'Unknown user';
	const isOwn = authUser?.id === comment.userId;
	const isResolved = !!comment.resolvedAt;
	const avatarSize = isReply ? 20 : 24;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onStartEdit = () => {
		setEditedMessage(comment.message);
		setIsEditing(true);
	};

	const onCancelEdit = () => {
		setIsEditing(false);
	};

	const onSaveEdit = () => {
		const trimmed = editedMessage.trim();
		if (!trimmed || trimmed === comment.message) {
			setIsEditing(false);
			return;
		}
		forge.updateFrameEntry({
			frameId: frame.id,
			entry: { ...comment, message: trimmed },
		});
		setIsEditing(false);
	};

	const onToggleResolved = () => {
		forge.updateFrameEntry({
			frameId: frame.id,
			entry: { ...comment, resolvedAt: isResolved ? null : new Date() },
		});
	};

	const onDelete = () => {
		forge.deleteFrameEntry({ frameId: frame.id, entryId: comment.id });
	};

	const onElementChipClick = () => {
		if (comment.elementId) {
			eventManager.emit('devigner-select-element', comment.elementId);
		}
	};

	const onEditKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSaveEdit();
		} else if (e.key === 'Escape') {
			e.preventDefault();
			onCancelEdit();
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<S.Comment $resolved={isResolved} $isReply={isReply}>
			<S.CommentHeader>
				<S.UserInfo>
					<ProfileIcon userId={comment.userId} size={avatarSize} />
					<S.UserId>{displayName}</S.UserId>
					{comment.elementId && !isReply && (
						<S.ElementChip onClick={onElementChipClick} title="Jump to element">
							<Icon type={IconType.SELECT} width={9} height={9} />
							element
						</S.ElementChip>
					)}
					{isResolved && <S.ResolvedBadge>Resolved</S.ResolvedBadge>}
				</S.UserInfo>
				{inspectorRightPanelWidth > 250 && (
					<S.Timestamp>{formatRelativeTime(new Date(comment.createdAt))}</S.Timestamp>
				)}
			</S.CommentHeader>
			<S.CommentContent>
				{isEditing ? (
					<MultilineInput
						value={editedMessage}
						onInput={(e) =>
							setEditedMessage((e.target as HTMLTextAreaElement).value)
						}
						onKeyDown={onEditKeyDown}
						minLines={2}
						renderBorder={true}
					/>
				) : (
					<ReactMarkdown>{comment.message}</ReactMarkdown>
				)}
			</S.CommentContent>
			{isEditing ? (
				<S.EditActions>
					<S.EditButton onClick={onCancelEdit}>Cancel</S.EditButton>
					<S.EditButton onClick={onSaveEdit}>Save</S.EditButton>
				</S.EditActions>
			) : (
				<S.Actions data-comment-actions>
					{!isReply && onStartReply && (
						<S.ActionButton onClick={onStartReply} title="Reply">
							<Icon type={IconType.CHAT} width={12} height={12} />
						</S.ActionButton>
					)}
					{isOwn && (
						<>
							<S.ActionButton
								onClick={onToggleResolved}
								title={isResolved ? 'Unresolve' : 'Resolve'}
							>
								<Icon type={IconType.CHECK} width={12} height={12} />
							</S.ActionButton>
							<S.ActionButton onClick={onStartEdit} title="Edit">
								<Icon type={IconType.EDIT} width={12} height={12} />
							</S.ActionButton>
							<S.ActionButton onClick={onDelete} title="Delete">
								<Icon type={IconType.TRASH} width={12} height={12} />
							</S.ActionButton>
						</>
					)}
				</S.Actions>
			)}
		</S.Comment>
	);
};

export default Comment;
